import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { getAddress, createAddress, updateAddress, deleteAddress, bulkHideAddresses, mergeAddresses } from "../addresses";
import type { AddressesStaticResponse } from "../map/types";
import type { UpdateAddressRequest } from "./types";

type StaticQueriesSnapshot = Array<[readonly unknown[], AddressesStaticResponse | undefined]>;

/**
 * Patches one address in all cached static map address responses (any bbox/tile).
 * Keeps React Query data aligned with optimistic map updates so layer merges don't revert isHidden/block/position.
 */
export const patchAddressInStaticMapCaches = (
    queryClient: QueryClient,
    addressId: string,
    request: UpdateAddressRequest
): void => {
    queryClient.setQueriesData<AddressesStaticResponse | undefined>(
        { queryKey: ["map", "addresses", "static"], exact: false },
        (old) => {
            if (!old?.features?.length) {
                return old;
            }

            let changed = false;
            let deltaHidden = 0;
            let deltaBlocked = 0;

            const features = old.features.map((f) => {
                const id = String(f.id ?? f.properties.addressPointId);
                if (id !== String(addressId)) {
                    return f;
                }

                let props = f.properties;
                let geometry = f.geometry;
                let touched = false;

                if (request.isHidden !== undefined && request.isHidden !== null) {
                    if (props.isHidden !== request.isHidden) {
                        deltaHidden += request.isHidden ? 1 : -1;
                        props = { ...props, isHidden: request.isHidden };
                        touched = true;
                    }
                }
                if (request.isBlocked !== undefined && request.isBlocked !== null) {
                    if (props.isBlocked !== request.isBlocked) {
                        deltaBlocked += request.isBlocked ? 1 : -1;
                        props = { ...props, isBlocked: request.isBlocked };
                        touched = true;
                    }
                }
                if (request.deliveryUnitCount !== undefined && request.deliveryUnitCount !== null) {
                    if (props.deliveryUnitCount !== request.deliveryUnitCount) {
                        props = { ...props, deliveryUnitCount: request.deliveryUnitCount };
                        touched = true;
                    }
                }

                if (
                    f.geometry?.type === "Point" &&
                    request.latitude != null &&
                    request.longitude != null
                ) {
                    const coords = f.geometry.coordinates as [number, number];
                    if (coords[0] !== request.longitude || coords[1] !== request.latitude) {
                        geometry = {
                            type: "Point",
                            coordinates: [request.longitude, request.latitude]
                        };
                        touched = true;
                    }
                }

                if (!touched) {
                    return f;
                }

                changed = true;
                return { ...f, geometry, properties: props };
            });

            if (!changed) {
                return old;
            }

            return {
                ...old,
                features,
                metadata: {
                    ...old.metadata,
                    hiddenCount: Math.max(0, old.metadata.hiddenCount + deltaHidden),
                    blockedCount: Math.max(0, old.metadata.blockedCount + deltaBlocked)
                }
            };
        }
    );
};

const snapshotStaticAddressQueries = (
    queryClient: QueryClient
): StaticQueriesSnapshot =>
    queryClient.getQueriesData<AddressesStaticResponse | undefined>({
        queryKey: ["map", "addresses", "static"],
        exact: false
    });

/**
 * React Query hook to fetch a single address
 */
export const useAddress = (id: string | null | undefined) => {
    return useQuery({
        queryKey: ["address", id],
        queryFn: () => getAddress(id!),
        enabled: !!id
    });
};

/**
 * React Query mutation hook to create a new address
 */
export const useCreateAddress = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createAddress,
        onSuccess: () => {
            // Invalidate map queries to refresh the map
            queryClient.invalidateQueries({ queryKey: ["map"] });
        }
    });
};

/**
 * React Query mutation hook to update an existing address
 */
export const useUpdateAddress = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, request }: { id: string; request: UpdateAddressRequest }) =>
            updateAddress(id, request),
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ["map", "addresses", "static"], exact: false });
            const previousStatic = snapshotStaticAddressQueries(queryClient);
            patchAddressInStaticMapCaches(queryClient, variables.id, variables.request);
            return { previousStatic };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousStatic) {
                for (const [queryKey, data] of context.previousStatic) {
                    queryClient.setQueryData(queryKey, data);
                }
            }
        },
        onSuccess: (_, variables) => {
            // Invalidate the specific address query immediately
            queryClient.invalidateQueries({ queryKey: ["address", variables.id] });

            // For position, block, and hide updates, don't invalidate map queries immediately
            // The optimistic visual update will remain until the next natural refresh
            // This prevents the optimistic update from being overwritten with stale data
            const isPositionUpdate = variables.request.latitude !== undefined || variables.request.longitude !== undefined;
            const isStatusUpdate = variables.request.isBlocked !== undefined || variables.request.isHidden !== undefined;

            if (isPositionUpdate || isStatusUpdate) {
                // Don't invalidate map queries for position/status updates - let the optimistic update persist
                // The map will refresh naturally when user zooms/pans or when staleTime expires
                // This ensures the new state stays visible until database is fully updated
                if (variables.request.isHidden === true && variables.request.campaignId) {
                    void queryClient.invalidateQueries({ queryKey: ["map", "addresses", "deliveries"] });
                    void queryClient.invalidateQueries({ queryKey: ["deliveries"] });
                }
            } else {
                // For other updates (metadata changes, etc.), invalidate immediately
                queryClient.invalidateQueries({ queryKey: ["map"] });

                // A deliveryUnitCount change with campaign scope re-syncs quantities on
                // already-recorded deliveries server-side; refresh delivery data too
                if (variables.request.deliveryUnitCount != null && variables.request.campaignId) {
                    void queryClient.invalidateQueries({ queryKey: ["deliveries"] });
                }
            }
        }
    });
};

/**
 * React Query mutation hook to delete an address
 */
export const useDeleteAddress = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteAddress,
        onSuccess: (_, addressId) => {
            // Invalidate map queries and remove the address from cache
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.removeQueries({ queryKey: ["address", addressId] });
        }
    });
};

/**
 * Bulk hide/show addresses selected on the map
 */
export const useBulkHideAddresses = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: bulkHideAddresses,
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ["map", "addresses", "static"], exact: false });
            const previousStatic = snapshotStaticAddressQueries(queryClient);
            for (const id of variables.addressPointIds) {
                patchAddressInStaticMapCaches(queryClient, id, {
                    isHidden: variables.isHidden ?? true
                });
            }
            return { previousStatic };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousStatic) {
                for (const [queryKey, data] of context.previousStatic) {
                    queryClient.setQueryData(queryKey, data);
                }
            }
        },
        onSuccess: (_, variables) => {
            if (variables.isHidden !== false && variables.campaignId) {
                void queryClient.invalidateQueries({ queryKey: ["map", "addresses", "deliveries"] });
                void queryClient.invalidateQueries({ queryKey: ["deliveries"] });
            }
        }
    });
};

/**
 * Merge multiple addresses into one point (sums delivery units)
 */
export const useMergeAddresses = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: mergeAddresses,
        onSuccess: (result, variables) => {
            const primary = result.primaryAddress;
            patchAddressInStaticMapCaches(queryClient, primary.id, {
                latitude: primary.latitude,
                longitude: primary.longitude,
                deliveryUnitCount: primary.deliveryUnitCount
            });
            for (const hiddenId of result.hiddenAddressPointIds) {
                patchAddressInStaticMapCaches(queryClient, hiddenId, { isHidden: true });
            }
            if (variables.campaignId) {
                void queryClient.invalidateQueries({ queryKey: ["map", "addresses", "deliveries"] });
                void queryClient.invalidateQueries({ queryKey: ["deliveries"] });
            }
            queryClient.invalidateQueries({ queryKey: ["address", primary.id] });
        }
    });
};